import * as THREE from 'three';
import { createActorMesh } from '../3d/assets/actors';

const SKELETON_FACING_ANGLES = [0, 60, 90, 120, 180, 240, 270, 300] as const;
type SkeletonFacingAngle = (typeof SKELETON_FACING_ANGLES)[number];
type SkeletonSpriteSet = Record<SkeletonFacingAngle, HTMLCanvasElement>;

const SKELETON_SPRITE_CACHE_BASE_SIZE = 128;
export const SKELETON_DRAW_SCALE = 1.35;

const skeletonSpriteCache = new Map<string, SkeletonSpriteSet>();
let offscreenRenderer: THREE.WebGLRenderer | null = null;
let offscreenScene: THREE.Scene | null = null;
let offscreenCamera: THREE.PerspectiveCamera | null = null;
let currentRendererSize = 0;

function normalizeRotation(degrees: number): number {
    return ((degrees % 360) + 360) % 360;
}

function shortestAngleDelta(a: number, b: number): number {
    let delta = normalizeRotation(a) - normalizeRotation(b);
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    return Math.abs(delta);
}

export function nearestSkeletonFacingAngle(rotation: number): SkeletonFacingAngle {
    let nearest: SkeletonFacingAngle = SKELETON_FACING_ANGLES[0];
    let best = shortestAngleDelta(rotation, nearest);
    for (let i = 1; i < SKELETON_FACING_ANGLES.length; i += 1) {
        const candidate = SKELETON_FACING_ANGLES[i];
        const dist = shortestAngleDelta(rotation, candidate);
        if (dist < best) {
            best = dist;
            nearest = candidate;
        }
    }
    return nearest;
}

function initOffscreenRenderer(size: number) {
    if (offscreenRenderer) {
        if (currentRendererSize !== size) {
            offscreenRenderer.setSize(size, size);
            currentRendererSize = size;
        }
        return;
    }

    currentRendererSize = size;
    offscreenRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    offscreenRenderer.setSize(size, size);
    offscreenRenderer.setPixelRatio(1); // We bake the pixel ratio into the size

    offscreenRenderer.setClearColor(0x000000, 0);

    offscreenScene = new THREE.Scene();

    // Match the lighting from the J menu so it looks identical
    const ambientLight = new THREE.HemisphereLight('#ffffff', '#444455', 1.0);
    ambientLight.position.set(0, 5, 0);
    offscreenScene.add(ambientLight);

    const mainLight = new THREE.DirectionalLight('#ffffff', 1.8);
    mainLight.position.set(3, 8, 5);
    offscreenScene.add(mainLight);

    offscreenCamera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    offscreenCamera.position.set(0, 0.9, 4.2); // Pull back so the whole model fits
    offscreenCamera.lookAt(0, 0.4, 0);
}

function getCacheScale(): number {
    if (typeof window === 'undefined' || !Number.isFinite(window.devicePixelRatio)) {
        return 1;
    }
    return Math.max(1, Math.ceil(window.devicePixelRatio));
}

export function getOrCreateSkeletonSpriteSet(
    color: string,
    accessoriesJson?: string,
    pose: 'idle' | 'jumping' = 'idle'
): SkeletonSpriteSet | null {
    const cacheScale = getCacheScale();
    const optionsKey = `${color}::${accessoriesJson ?? ''}::${pose}::${cacheScale}`;

    const existing = skeletonSpriteCache.get(optionsKey);
    if (existing) {
        return existing;
    }

    if (typeof document === 'undefined') {
        return null; // SSR safety
    }

    const baseSize = SKELETON_SPRITE_CACHE_BASE_SIZE * cacheScale;
    initOffscreenRenderer(baseSize);

    // Build the 3D model
    const bundle = createActorMesh({
        kind: 'mob',
        entityType: 'skeleton',
        color,
        accessoriesJson,
        pose,
    });

    // Temporarily add to our offscreen scene
    offscreenScene!.add(bundle.group);

    const spriteSet = {} as unknown as SkeletonSpriteSet;

    for (let i = 0; i < SKELETON_FACING_ANGLES.length; i += 1) {
        const angle = SKELETON_FACING_ANGLES[i];

        // Rotate the model to face the right way. Note: 3D rotation vs 2D top-down angle
        // In our 2D game, 0 is right, 90 is down, 180 is left, 270 is up.
        // In our 3D UI, facing the camera usually means rotation.y = 0.
        // We adjust the rotation to map the 2D logical angle to the 3D visual angle.
        bundle.group.rotation.y = (-angle * Math.PI) / 180 + Math.PI;

        offscreenRenderer!.clear();
        offscreenRenderer!.render(offscreenScene!, offscreenCamera!);

        // Extract the frame to a persistent canvas element
        const canvas = document.createElement('canvas');
        canvas.width = baseSize;
        canvas.height = baseSize;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        // WebGL gives us an upside-down image compared to 2D canvas sometimes depending on read pixels, 
        // but just drawing the webgl dom element to a 2d context handles it natively.
        ctx.drawImage(offscreenRenderer!.domElement, 0, 0);

        spriteSet[angle as SkeletonFacingAngle] = canvas;
    }

    // Cleanup scene. Geometries come from the shared actor geometry cache,
    // so they must stay alive for later 2D and 3D renders.
    offscreenScene!.remove(bundle.group);

    // Dispose per-instance materials since the baked sprite no longer needs them.
    for (const mat of bundle.materials) {
        if (Array.isArray(mat)) {
            mat.forEach(m => m.dispose());
        } else {
            mat.dispose();
        }
    }

    skeletonSpriteCache.set(optionsKey, spriteSet);
    return spriteSet;
}
